import { WORKFLOW_NOTES } from "@sagot/constants";
import type { Definition, Note, Notes } from "./types";

/** Sticky notes (`definition.notes`): a note belongs to a path, or to the workflow as a whole when it has none. Only the console ever sees them. */

/** The notes in the order they were written. */
export function notesOf(definition: Definition): { id: string; note: Note }[] {
  return Object.entries(definition.notes ?? {}).map(([id, note]) => ({ id, note }));
}

/** The notes anchored to one path (`pathId`), or the workflow-level ones (`null`). */
export function notesAt(definition: Definition, pathId: string | null): { id: string; note: Note }[] {
  return notesOf(definition).filter(({ note }) => (note.path ?? null) === pathId);
}

export function canAddNote(definition: Definition): boolean {
  return Object.keys(definition.notes ?? {}).length < WORKFLOW_NOTES.max;
}

/** `note_1`, `note_2` ... (the backend note id has the pattern of a step id). */
export function freshNoteId(notes: Notes | undefined): string {
  const taken = new Set(Object.keys(notes ?? {}));
  let n = 1;
  while (taken.has(`note_${String(n)}`)) n += 1;
  return `note_${String(n)}`;
}

/** The definition with `notes` set (no notes at all leaves the key out, so older drafts stay as they were). */
export function withNotes(definition: Definition, notes: Notes): Definition {
  const rest: Definition = { ...definition };
  Reflect.deleteProperty(rest, "notes");
  return Object.keys(notes).length > 0 ? { ...rest, notes } : rest;
}

/** A new note; `null` when `NOTES_MAX` notes exist or the text is empty. */
export function addNote(definition: Definition, text: string, pathId?: string): { definition: Definition; id: string } | null {
  const trimmed = text.trim().slice(0, WORKFLOW_NOTES.textMax);
  if (!trimmed || !canAddNote(definition)) return null;
  const id = freshNoteId(definition.notes);
  const note: Note = pathId ? { text: trimmed, path: pathId } : { text: trimmed };
  return { definition: withNotes(definition, { ...definition.notes, [id]: note }), id };
}

/** A note with new text; an empty text removes the note. */
export function editNote(definition: Definition, id: string, text: string): Definition {
  const note = definition.notes?.[id];
  if (!note) return definition;
  const trimmed = text.trim().slice(0, WORKFLOW_NOTES.textMax);
  if (!trimmed) return removeNote(definition, id);
  return withNotes(definition, { ...definition.notes, [id]: { ...note, text: trimmed } });
}

export function removeNote(definition: Definition, id: string): Definition {
  return withNotes(definition, Object.fromEntries(Object.entries(definition.notes ?? {}).filter(([key]) => key !== id)));
}

/** The notes of a removed path become notes of the workflow, so nothing the author wrote is lost with it. */
export function releaseNotesOf(definition: Definition, pathId: string): Definition {
  const notes = definition.notes;
  if (!notes || !Object.values(notes).some((note) => note.path === pathId)) return definition;
  return withNotes(definition, Object.fromEntries(Object.entries(notes).map(([id, note]) => [id, note.path === pathId ? { text: note.text } : note])));
}

/** Copies the notes of `from` onto `to` (a duplicated path), as far as the limit allows. */
export function copyNotes(definition: Definition, from: string, to: string): Definition {
  let next = definition;
  for (const { note } of notesAt(definition, from)) next = addNote(next, note.text, to)?.definition ?? next;
  return next;
}
